import mapPlainTextIndex from './mapPlainTextIndex'
import getPlainText from './getPlainText'
import spliceString from './spliceString'
import iterateMentionsMarkup from './iterateMentionsMarkup'

/**
 * Apply a plain-text edit to marked-up value by finding the single contiguous
 * changed region via common prefix/suffix.
 *
 * More reliable than selection-based diffs for browser spellcheck replacements
 * and other non-typing edits where selectionStart/End before the change are wrong.
 */
const applyPlainTextDiffToValue = (value, plainTextValue, config) => {
  const oldPlainTextValue = getPlainText(value, config)
  if (oldPlainTextValue === plainTextValue) {
    return value
  }

  let spliceStart = 0
  const minLen = Math.min(oldPlainTextValue.length, plainTextValue.length)
  while (
    spliceStart < minLen &&
    oldPlainTextValue[spliceStart] === plainTextValue[spliceStart]
  ) {
    spliceStart++
  }

  let oldSpliceEnd = oldPlainTextValue.length
  let newSpliceEnd = plainTextValue.length
  while (
    oldSpliceEnd > spliceStart &&
    newSpliceEnd > spliceStart &&
    oldPlainTextValue[oldSpliceEnd - 1] === plainTextValue[newSpliceEnd - 1]
  ) {
    oldSpliceEnd--
    newSpliceEnd--
  }

  // A mention is atomic markup. If the browser edited inside its displayed text,
  // replace the whole mention with the browser's corresponding plain-text range.
  // This removes the mention metadata without dropping part of the corrected text.
  const originalSpliceStart = spliceStart
  const originalOldSpliceEnd = oldSpliceEnd
  iterateMentionsMarkup(
    value,
    config,
    (match, index, mentionStart, id, display) => {
      const mentionEnd = mentionStart + display.length
      const insertsInsideMention =
        originalSpliceStart === originalOldSpliceEnd &&
        originalSpliceStart > mentionStart &&
        originalSpliceStart < mentionEnd
      const overlapsMention =
        originalSpliceStart < mentionEnd &&
        originalOldSpliceEnd > mentionStart

      if (insertsInsideMention || overlapsMention) {
        const expandStartBy = Math.max(0, spliceStart - mentionStart)
        const expandEndBy = Math.max(0, mentionEnd - oldSpliceEnd)
        spliceStart -= expandStartBy
        oldSpliceEnd += expandEndBy
        newSpliceEnd += expandEndBy
      }
    }
  )

  const insert = plainTextValue.slice(spliceStart, newSpliceEnd)
  const mappedSpliceStart = mapPlainTextIndex(value, config, spliceStart, 'START')
  const mappedSpliceEnd = mapPlainTextIndex(value, config, oldSpliceEnd, 'END')

  return spliceString(value, mappedSpliceStart, mappedSpliceEnd, insert)
}

export default applyPlainTextDiffToValue
